import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Track Tab Skeleton - STABLE LAYOUT: Matches exact TrackTab card dimensions
 * Fixed heights prevent layout shift during hydration
 */
export const TrackTabSkeleton = () => {
  return (
    // Stable min-height to prevent layout shift
    <div className="space-y-4 animate-fade-in" style={{ minHeight: '320px' }}>
      {/* Header - matches TrackTab header */}
      <div>
        <Skeleton className="h-8 w-40 bg-primary/10 mb-1" />
        <Skeleton className="h-4 w-28 bg-primary/10" />
      </div>

      {/* Quick Log Cards Grid - EXACT HEIGHT: 140px per card (matches TrackTab h-[140px]) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card 
            key={i} 
            className="overflow-hidden border-0 rounded-2xl h-[140px]"
            style={{ height: '140px' }} // Enforce exact height
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary/5" />
            <CardHeader className="p-4 h-full flex items-center justify-center">
              <div className="flex flex-col items-center text-center gap-2">
                <Skeleton className="h-11 w-11 rounded-2xl bg-primary/15" />
                <div>
                  <Skeleton className="h-4 w-16 bg-primary/10 mb-1 mx-auto" />
                  <Skeleton className="h-3 w-14 bg-primary/10 mx-auto" />
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
};

/**
 * Meds Tab Skeleton - STABLE LAYOUT: Matches medication card dimensions
 */
export const MedsTabSkeleton = () => {
  return (
    <div className="space-y-4 animate-fade-in" style={{ minHeight: '400px' }}>
      {/* Daily Schedule Card placeholder */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/3 border-0">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <Skeleton className="h-5 w-32 bg-primary/10" />
            <Skeleton className="h-4 w-20 bg-primary/10" />
          </div>
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 flex-1 rounded-lg bg-primary/10" />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Header Actions - stable height */}
      <div className="flex justify-between items-center h-10">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 bg-primary/10" />
          <Skeleton className="h-6 w-36 bg-primary/10" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 bg-primary/10 rounded-lg" />
          <Skeleton className="h-9 w-32 bg-primary/10 rounded-lg" />
        </div>
      </div>

      {/* Search input */}
      <Skeleton className="h-10 w-full bg-primary/10 rounded-lg" />

      {/* Medication Cards - 3 items with exact heights */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-gradient-to-br from-primary/5 to-primary/3 border-0" style={{ minHeight: '88px' }}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <Skeleton className="h-14 w-14 rounded-lg bg-primary/10 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-36 bg-primary/10" />
                  <Skeleton className="h-4 w-28 bg-primary/10" />
                  <Skeleton className="h-4 w-48 bg-primary/10" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

/**
 * Appointments Tab Skeleton - STABLE LAYOUT: Matches appointment list dimensions
 */
export const AppointmentsTabSkeleton = () => {
  return (
    <div className="space-y-4 animate-fade-in" style={{ minHeight: '500px' }}>
      {/* Reminders Widget placeholder - stable height */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/3 border-0" style={{ minHeight: '80px' }}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-lg bg-primary/10" />
              <div>
                <Skeleton className="h-5 w-36 bg-primary/10 mb-1" />
                <Skeleton className="h-4 w-48 bg-primary/10" />
              </div>
            </div>
            <Skeleton className="h-8 w-20 bg-primary/10 rounded" />
          </div>
        </CardContent>
      </Card>

      {/* Tabs Navigation - fixed height */}
      <div className="h-12 bg-muted/50 rounded-lg flex items-center p-1">
        <Skeleton className="h-9 flex-1 bg-primary/10 rounded" />
        <Skeleton className="h-9 flex-1 bg-primary/5 rounded mx-1" />
        <Skeleton className="h-9 flex-1 bg-primary/5 rounded" />
      </div>

      {/* Appointments Content Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/3 border-0">
        <CardContent className="p-4 sm:p-6">
          {/* Section header */}
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="h-6 w-40 bg-primary/10" />
            <Skeleton className="h-9 w-32 bg-primary/10 rounded-lg" />
          </div>
          
          {/* Appointment list items - reserve space for 5 items */}
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-4 rounded-lg bg-card/50 space-y-2" style={{ minHeight: '76px' }}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <Skeleton className="h-5 w-44 bg-primary/10 mb-2" />
                    <Skeleton className="h-4 w-28 bg-primary/10" />
                  </div>
                  <Skeleton className="h-8 w-8 rounded bg-primary/10" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * More Tab Skeleton - STABLE LAYOUT: Matches exact MoreTab dimensions
 * Fixed heights prevent layout shift during hydration
 */
export const MoreTabSkeleton = () => {
  return (
    // Stable min-height matching real MoreTab content
    <div className="lg:pb-6 px-1 sm:px-0 space-y-4 animate-fade-in" style={{ minHeight: '600px' }}>
      {/* Header Section - matches MoreTab header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-1 w-12 bg-gradient-to-r from-purple-500/30 to-violet-500/30 rounded-full" />
          <Skeleton className="h-9 w-20 bg-primary/10" />
        </div>
        <Skeleton className="h-5 w-72 bg-primary/10" />
      </div>
      
      {/* Premium CTA Card placeholder - stable height */}
      <Card className="overflow-hidden border-purple-500/30 bg-gradient-to-br from-purple-950/30 to-purple-900/20" style={{ minHeight: '100px' }}>
        <div className="p-4">
          <div className="flex items-start gap-4">
            <Skeleton className="h-11 w-11 rounded-xl bg-yellow-500/20" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-40 bg-primary/10" />
              <Skeleton className="h-4 w-64 bg-primary/10" />
              <Skeleton className="h-9 w-24 bg-primary/10 rounded" />
            </div>
          </div>
        </div>
      </Card>

      {/* Section Header */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-24 bg-primary/10" />
        <div className="h-px flex-1 bg-gradient-to-r from-purple-500/20 to-transparent" />
      </div>

      {/* Feature Cards Grid - 2x2, exact height: 140px per card */}
      <div className="grid grid-cols-2 gap-2.5">
        {[1, 2, 3, 4].map((i) => (
          <Card 
            key={i} 
            className="overflow-hidden border-0 rounded-2xl h-[140px] bg-gradient-to-br from-primary/10 to-primary/5"
            style={{ height: '140px' }}
          >
            <div className="relative flex flex-col items-center justify-start gap-2 py-3 px-2.5 h-full">
              <Skeleton className="h-11 w-11 rounded-2xl bg-primary/15" />
              <div className="flex flex-col items-center gap-1">
                <Skeleton className="h-4 w-24 bg-primary/10" />
                <Skeleton className="h-3 w-32 bg-primary/10" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

/**
 * Beta Tab Skeleton - STABLE LAYOUT: Matches exact BetaTab dimensions
 * Fixed heights prevent layout shift during hydration
 */
export const BetaTabSkeleton = () => {
  return (
    // Stable min-height matching real BetaTab content
    <div className="space-y-6 animate-fade-in" style={{ minHeight: '700px' }}>
      {/* Header Section - matches BetaTab header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-9 w-40 bg-primary/10" />
          <Skeleton className="h-5 w-80 bg-primary/10" />
          <Skeleton className="h-4 w-64 bg-primary/10" />
        </div>
        <Skeleton className="h-9 w-24 bg-primary/10 rounded" />
      </div>

      {/* Main Feedback Card - stable height */}
      <Card className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 border-primary/20" style={{ minHeight: '320px' }}>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-full bg-primary/10" />
            <div className="space-y-1">
              <Skeleton className="h-6 w-44 bg-primary/10" />
              <Skeleton className="h-4 w-64 bg-primary/10" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info box */}
          <div className="p-4 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg" style={{ minHeight: '120px' }}>
            <Skeleton className="h-5 w-32 bg-blue-200/50 dark:bg-blue-800/50 mb-3" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full bg-blue-200/30 dark:bg-blue-800/30" />
              <Skeleton className="h-4 w-3/4 bg-blue-200/30 dark:bg-blue-800/30" />
              <Skeleton className="h-4 w-5/6 bg-blue-200/30 dark:bg-blue-800/30" />
            </div>
          </div>
          
          {/* Button area */}
          <div className="flex flex-col items-center gap-3 pt-2">
            <Skeleton className="h-12 w-52 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded" />
            <Skeleton className="h-4 w-80 bg-primary/10" />
          </div>
        </CardContent>
      </Card>

      {/* Tips Card - stable height */}
      <Card style={{ minHeight: '200px' }}>
        <CardHeader>
          <Skeleton className="h-6 w-64 bg-primary/10" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-start gap-2">
              <Skeleton className="h-2 w-2 rounded-full bg-primary/20 mt-2" />
              <Skeleton className="h-4 flex-1 bg-primary/10" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
